import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  Settings as SettingsIcon,
  Building2,
  Bell,
  Shield,
  Globe,
  Mail,
  Database,
  Clock,
  Lock,
  Key,
  Users,
  FileText,
  Save,
  RotateCcw,
  AlertTriangle,
  CheckCircle,
  Download,
  Upload,
  Calendar,
  X,
  Plus,
  Ban,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { Input } from '@components/forms/Input';
import { Select } from '@components/forms/Select';
import { Checkbox } from '@components/forms/Checkbox';

type SettingsTab = 'general' | 'notifications' | 'security' | 'system' | 'backup' | 'calendar';

export const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // General Settings State
  const [generalSettings, setGeneralSettings] = useState({
    organizationName: 'NCAA Digital Enrolment',
    supportEmail: 'support@ncaa-enrolment.org',
    supportPhone: '+1 (555) 123-4567',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    language: 'en',
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: true,
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailEnabled: true,
    smsEnabled: false,
    // Admin notifications
    notifyNewApplicant: true,
    notifyApplicationSubmitted: true,
    notifyAppointmentBooked: true,
    notifyDocumentUploaded: true,
    // Applicant notifications
    applicantWelcomeEmail: true,
    applicantStatusUpdates: true,
    applicantAppointmentReminders: true,
    reminderDaysBefore: '2',
    // System notifications
    notifySystemErrors: true,
    notifySecurityAlerts: true,
    dailyReportEmail: true,
    weeklyAnalyticsReport: true,
  });

  // Security Settings State
  const [securitySettings, setSecuritySettings] = useState({
    passwordMinLength: '8',
    passwordRequireUppercase: true,
    passwordRequireNumbers: true,
    passwordRequireSpecialChars: true,
    sessionTimeout: '30',
    maxLoginAttempts: '5',
    lockoutDuration: '15',
    twoFactorEnabled: false,
    ipWhitelisting: false,
    allowedIPs: '',
    forcePasswordChange: '90',
  });

  // System Settings State
  const [systemSettings, setSystemSettings] = useState({
    maxFileSize: '10',
    allowedFileTypes: '.pdf,.jpg,.jpeg,.png,.doc,.docx',
    applicationsPerPage: '10',
    appointmentSlotDuration: '30',
    maxConcurrentBookings: '3',
    advanceBookingDays: '60',
    cancellationHours: '24',
    enableAuditLogs: true,
    logRetentionDays: '90',
    enableAnalytics: true,
  });

  // Backup Settings State
  const [backupSettings, setBackupSettings] = useState({
    autoBackupEnabled: true,
    backupFrequency: 'daily',
    backupTime: '02:00',
    retentionPeriod: '30',
    lastBackupDate: '2025-01-07T02:00:00Z',
    backupStatus: 'success',
  });

  // Calendar Settings State
  const [blockedDates, setBlockedDates] = useState<Array<{ date: string; reason: string; id: number }>>([
    { id: 1, date: '2025-01-15', reason: 'Public Holiday - New Year\'s Day' },
    { id: 2, date: '2025-12-25', reason: 'Christmas Day' },
    { id: 3, date: '2025-12-26', reason: 'Boxing Day' },
  ]);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [newBlockedReason, setNewBlockedReason] = useState('');

  const tabs = [
    { id: 'general' as const, label: 'General', icon: <Building2 className="h-4 w-4" /> },
    { id: 'notifications' as const, label: 'Notifications', icon: <Bell className="h-4 w-4" /> },
    { id: 'security' as const, label: 'Security', icon: <Shield className="h-4 w-4" /> },
    { id: 'system' as const, label: 'System', icon: <Database className="h-4 w-4" /> },
    { id: 'backup' as const, label: 'Backup', icon: <Download className="h-4 w-4" /> },
    { id: 'calendar' as const, label: 'Calendar', icon: <Calendar className="h-4 w-4" /> },
  ];

  const handleSaveSettings = () => {
    // Mock save operation
    console.log('💾 Mock: Saving settings', {
      general: generalSettings,
      notifications: notificationSettings,
      security: securitySettings,
      system: systemSettings,
      backup: backupSettings,
      blockedDates: blockedDates,
    });

    toast.success('Settings saved successfully');
    setHasUnsavedChanges(false);
  };

  const handleAddBlockedDate = () => {
    if (!newBlockedDate) {
      toast.error('Please select a date');
      return;
    }
    if (!newBlockedReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    // Check if date is already blocked
    if (blockedDates.some((d) => d.date === newBlockedDate)) {
      toast.error('This date is already blocked');
      return;
    }

    const newId = Math.max(...blockedDates.map((d) => d.id), 0) + 1;
    setBlockedDates([...blockedDates, { id: newId, date: newBlockedDate, reason: newBlockedReason }]);
    setNewBlockedDate('');
    setNewBlockedReason('');
    setHasUnsavedChanges(true);
    toast.success('Date blocked successfully');
    console.log('🚫 Mock: Blocked date', newBlockedDate, newBlockedReason);
  };

  const handleRemoveBlockedDate = (id: number) => {
    const date = blockedDates.find((d) => d.id === id);
    if (window.confirm(`Remove blocked date: ${date?.date}?`)) {
      setBlockedDates(blockedDates.filter((d) => d.id !== id));
      setHasUnsavedChanges(true);
      toast.success('Blocked date removed');
      console.log('✅ Mock: Unblocked date', id);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values? This action cannot be undone.')) {
      // Reset to default values
      toast.success('Settings reset to defaults');
      setHasUnsavedChanges(false);
      console.log('🔄 Mock: Settings reset to defaults');
    }
  };

  const handleExportSettings = () => {
    console.log('📥 Mock: Export settings as JSON');
    toast.success('Settings exported successfully');
  };

  const handleImportSettings = () => {
    console.log('📤 Mock: Import settings from JSON');
    toast.success('Settings imported successfully');
  };

  const handleBackupNow = () => {
    console.log('💾 Mock: Initiating manual backup');
    toast.success('Backup initiated successfully');
    setBackupSettings({
      ...backupSettings,
      lastBackupDate: new Date().toISOString(),
      backupStatus: 'success',
    });
  };

  const handleRestoreBackup = () => {
    if (window.confirm('Are you sure you want to restore from backup? This will overwrite current data.')) {
      console.log('♻️ Mock: Restoring from backup');
      toast.success('Restore initiated successfully');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ncaa-dark flex items-center gap-3">
              <SettingsIcon className="h-8 w-8 text-ncaa-primary" />
              System Settings
            </h1>
            <p className="text-ncaa-medium mt-1">Configure and manage system preferences</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleExportSettings} icon={<Download className="h-4 w-4" />}>
              Export
            </Button>
            <Button variant="outline" onClick={handleImportSettings} icon={<Upload className="h-4 w-4" />}>
              Import
            </Button>
            <Button
              variant="outline"
              onClick={handleResetSettings}
              icon={<RotateCcw className="h-4 w-4" />}
            >
              Reset
            </Button>
            <Button
              onClick={handleSaveSettings}
              icon={<Save className="h-4 w-4" />}
              disabled={!hasUnsavedChanges}
            >
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            You have unsaved changes. Click "Save Changes" to apply them.
          </p>
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar Tabs */}
        <div className="w-64 flex-shrink-0">
          <Card className="p-2">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-ncaa-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {tab.icon}
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <Card>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className="text-xl font-bold text-ncaa-dark flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-ncaa-primary" />
                    General Settings
                  </h2>
                  <p className="text-sm text-ncaa-medium mt-1">
                    Configure basic system information and preferences
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Organization Info */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Organization Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Organization Name"
                        value={generalSettings.organizationName}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, organizationName: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <Input
                        label="Support Email"
                        type="email"
                        icon={<Mail className="h-4 w-4" />}
                        value={generalSettings.supportEmail}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, supportEmail: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <Input
                        label="Support Phone"
                        type="tel"
                        value={generalSettings.supportPhone}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, supportPhone: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                  </div>

                  {/* Regional Settings */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Regional Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Select
                        label="Timezone"
                        value={generalSettings.timezone}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, timezone: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        options={[
                          { value: 'America/New_York', label: 'Eastern Time (ET)' },
                          { value: 'America/Chicago', label: 'Central Time (CT)' },
                          { value: 'America/Denver', label: 'Mountain Time (MT)' },
                          { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
                        ]}
                      />
                      <Select
                        label="Date Format"
                        value={generalSettings.dateFormat}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, dateFormat: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        options={[
                          { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                          { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                          { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                        ]}
                      />
                      <Select
                        label="Language"
                        value={generalSettings.language}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, language: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        options={[
                          { value: 'en', label: 'English' },
                          { value: 'es', label: 'Spanish' },
                          { value: 'fr', label: 'French' },
                        ]}
                      />
                    </div>
                  </div>

                  {/* System Options */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">System Options</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="Maintenance Mode"
                        checked={generalSettings.maintenanceMode}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, maintenanceMode: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Disable public access for system maintenance"
                      />
                      <Checkbox
                        label="Allow Registration"
                        checked={generalSettings.allowRegistration}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, allowRegistration: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Allow new users to register accounts"
                      />
                      <Checkbox
                        label="Require Email Verification"
                        checked={generalSettings.requireEmailVerification}
                        onChange={(e) => {
                          setGeneralSettings({ ...generalSettings, requireEmailVerification: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Require users to verify their email address"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <Card>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className="text-xl font-bold text-ncaa-dark flex items-center gap-2">
                    <Bell className="h-5 w-5 text-ncaa-primary" />
                    Notification Settings
                  </h2>
                  <p className="text-sm text-ncaa-medium mt-1">
                    Configure email and SMS notifications for system events
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Notification Channels */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Channels</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="Email Notifications"
                        checked={notificationSettings.emailEnabled}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, emailEnabled: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Send notifications via email"
                      />
                      <Checkbox
                        label="SMS Notifications"
                        checked={notificationSettings.smsEnabled}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, smsEnabled: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Send notifications via SMS (requires SMS gateway configuration)"
                      />
                    </div>
                  </div>

                  {/* Admin Notifications */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Notifications</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="New Applicant Registered"
                        checked={notificationSettings.notifyNewApplicant}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, notifyNewApplicant: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <Checkbox
                        label="Application Submitted"
                        checked={notificationSettings.notifyApplicationSubmitted}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, notifyApplicationSubmitted: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <Checkbox
                        label="Appointment Booked"
                        checked={notificationSettings.notifyAppointmentBooked}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, notifyAppointmentBooked: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <Checkbox
                        label="Document Uploaded"
                        checked={notificationSettings.notifyDocumentUploaded}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, notifyDocumentUploaded: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                  </div>

                  {/* Applicant Notifications */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Applicant Notifications</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="Welcome Email"
                        checked={notificationSettings.applicantWelcomeEmail}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, applicantWelcomeEmail: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Send welcome email upon registration"
                      />
                      <Checkbox
                        label="Status Updates"
                        checked={notificationSettings.applicantStatusUpdates}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, applicantStatusUpdates: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Notify when application status changes"
                      />
                      <Checkbox
                        label="Appointment Reminders"
                        checked={notificationSettings.applicantAppointmentReminders}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, applicantAppointmentReminders: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Send reminders before scheduled appointments"
                      />
                      <div className="ml-6">
                        <Input
                          label="Reminder Days Before"
                          type="number"
                          min="1"
                          max="7"
                          value={notificationSettings.reminderDaysBefore}
                          onChange={(e) => {
                            setNotificationSettings({ ...notificationSettings, reminderDaysBefore: e.target.value });
                            setHasUnsavedChanges(true);
                          }}
                          helpText="Send reminder this many days before appointment"
                          disabled={!notificationSettings.applicantAppointmentReminders}
                        />
                      </div>
                    </div>
                  </div>

                  {/* System Notifications */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">System Notifications</h3>
                    <div className="space-y-3">
                      <Checkbox
                        label="System Errors"
                        checked={notificationSettings.notifySystemErrors}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, notifySystemErrors: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Notify admins of critical system errors"
                      />
                      <Checkbox
                        label="Security Alerts"
                        checked={notificationSettings.notifySecurityAlerts}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, notifySecurityAlerts: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Notify admins of security-related events"
                      />
                      <Checkbox
                        label="Daily Report Email"
                        checked={notificationSettings.dailyReportEmail}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, dailyReportEmail: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Send daily summary report to admins"
                      />
                      <Checkbox
                        label="Weekly Analytics Report"
                        checked={notificationSettings.weeklyAnalyticsReport}
                        onChange={(e) => {
                          setNotificationSettings({ ...notificationSettings, weeklyAnalyticsReport: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Send weekly analytics digest to admins"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <Card>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className="text-xl font-bold text-ncaa-dark flex items-center gap-2">
                    <Shield className="h-5 w-5 text-ncaa-primary" />
                    Security Settings
                  </h2>
                  <p className="text-sm text-ncaa-medium mt-1">
                    Configure password policies and security measures
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Password Policy */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Policy</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <Input
                        label="Minimum Password Length"
                        type="number"
                        min="6"
                        max="32"
                        icon={<Lock className="h-4 w-4" />}
                        value={securitySettings.passwordMinLength}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, passwordMinLength: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Characters"
                      />
                      <Input
                        label="Force Password Change"
                        type="number"
                        min="0"
                        max="365"
                        icon={<Clock className="h-4 w-4" />}
                        value={securitySettings.forcePasswordChange}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, forcePasswordChange: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Days (0 = never)"
                      />
                    </div>
                    <div className="space-y-3">
                      <Checkbox
                        label="Require Uppercase Letters"
                        checked={securitySettings.passwordRequireUppercase}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, passwordRequireUppercase: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <Checkbox
                        label="Require Numbers"
                        checked={securitySettings.passwordRequireNumbers}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, passwordRequireNumbers: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <Checkbox
                        label="Require Special Characters"
                        checked={securitySettings.passwordRequireSpecialChars}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, passwordRequireSpecialChars: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                      />
                    </div>
                  </div>

                  {/* Session Management */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Management</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Session Timeout"
                        type="number"
                        min="5"
                        max="240"
                        icon={<Clock className="h-4 w-4" />}
                        value={securitySettings.sessionTimeout}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, sessionTimeout: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Minutes"
                      />
                      <Input
                        label="Maximum Login Attempts"
                        type="number"
                        min="3"
                        max="10"
                        icon={<Key className="h-4 w-4" />}
                        value={securitySettings.maxLoginAttempts}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, maxLoginAttempts: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Attempts before lockout"
                      />
                      <Input
                        label="Lockout Duration"
                        type="number"
                        min="5"
                        max="120"
                        icon={<Lock className="h-4 w-4" />}
                        value={securitySettings.lockoutDuration}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, lockoutDuration: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Minutes"
                      />
                    </div>
                  </div>

                  {/* Advanced Security */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Security</h3>
                    <div className="space-y-4">
                      <Checkbox
                        label="Two-Factor Authentication"
                        checked={securitySettings.twoFactorEnabled}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, twoFactorEnabled: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Require 2FA for all admin users"
                      />
                      <Checkbox
                        label="IP Whitelisting"
                        checked={securitySettings.ipWhitelisting}
                        onChange={(e) => {
                          setSecuritySettings({ ...securitySettings, ipWhitelisting: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Restrict access to specified IP addresses"
                      />
                      {securitySettings.ipWhitelisting && (
                        <div className="ml-6">
                          <Input
                            label="Allowed IP Addresses"
                            value={securitySettings.allowedIPs}
                            onChange={(e) => {
                              setSecuritySettings({ ...securitySettings, allowedIPs: e.target.value });
                              setHasUnsavedChanges(true);
                            }}
                            placeholder="192.168.1.1, 10.0.0.1"
                            helpText="Comma-separated list of IP addresses"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* System Settings */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <Card>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className="text-xl font-bold text-ncaa-dark flex items-center gap-2">
                    <Database className="h-5 w-5 text-ncaa-primary" />
                    System Settings
                  </h2>
                  <p className="text-sm text-ncaa-medium mt-1">
                    Configure system limits and operational parameters
                  </p>
                </div>

                <div className="space-y-6">
                  {/* File Management */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">File Management</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Maximum File Size"
                        type="number"
                        min="1"
                        max="100"
                        icon={<FileText className="h-4 w-4" />}
                        value={systemSettings.maxFileSize}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, maxFileSize: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="MB"
                      />
                      <Input
                        label="Allowed File Types"
                        value={systemSettings.allowedFileTypes}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, allowedFileTypes: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Comma-separated file extensions"
                      />
                    </div>
                  </div>

                  {/* Application Settings */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Applications Per Page"
                        type="number"
                        min="5"
                        max="100"
                        value={systemSettings.applicationsPerPage}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, applicationsPerPage: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Items"
                      />
                    </div>
                  </div>

                  {/* Appointment Settings */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointment Settings</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Appointment Slot Duration"
                        type="number"
                        min="15"
                        max="120"
                        icon={<Clock className="h-4 w-4" />}
                        value={systemSettings.appointmentSlotDuration}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, appointmentSlotDuration: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Minutes"
                      />
                      <Input
                        label="Max Concurrent Bookings"
                        type="number"
                        min="1"
                        max="10"
                        icon={<Users className="h-4 w-4" />}
                        value={systemSettings.maxConcurrentBookings}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, maxConcurrentBookings: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Per applicant"
                      />
                      <Input
                        label="Advance Booking Period"
                        type="number"
                        min="7"
                        max="180"
                        value={systemSettings.advanceBookingDays}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, advanceBookingDays: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Days ahead"
                      />
                      <Input
                        label="Cancellation Notice"
                        type="number"
                        min="1"
                        max="72"
                        value={systemSettings.cancellationHours}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, cancellationHours: e.target.value });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Hours before appointment"
                      />
                    </div>
                  </div>

                  {/* Logging & Analytics */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Logging & Analytics</h3>
                    <div className="space-y-4">
                      <Checkbox
                        label="Enable Audit Logs"
                        checked={systemSettings.enableAuditLogs}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, enableAuditLogs: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Track all system actions and events"
                      />
                      {systemSettings.enableAuditLogs && (
                        <div className="ml-6">
                          <Input
                            label="Log Retention Period"
                            type="number"
                            min="7"
                            max="365"
                            value={systemSettings.logRetentionDays}
                            onChange={(e) => {
                              setSystemSettings({ ...systemSettings, logRetentionDays: e.target.value });
                              setHasUnsavedChanges(true);
                            }}
                            helpText="Days"
                          />
                        </div>
                      )}
                      <Checkbox
                        label="Enable Analytics"
                        checked={systemSettings.enableAnalytics}
                        onChange={(e) => {
                          setSystemSettings({ ...systemSettings, enableAnalytics: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Collect and analyze usage statistics"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Backup Settings */}
          {activeTab === 'backup' && (
            <div className="space-y-6">
              <Card>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className="text-xl font-bold text-ncaa-dark flex items-center gap-2">
                    <Download className="h-5 w-5 text-ncaa-primary" />
                    Backup & Restore
                  </h2>
                  <p className="text-sm text-ncaa-medium mt-1">
                    Configure automated backups and restore data
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Backup Status */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Last Backup Status</h3>
                        <div className="flex items-center gap-3">
                          {backupSettings.backupStatus === 'success' ? (
                            <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>
                              Success
                            </Badge>
                          ) : (
                            <Badge variant="error" icon={<AlertTriangle className="h-3 w-3" />}>
                              Failed
                            </Badge>
                          )}
                          <span className="text-sm text-gray-600">
                            {formatDate(backupSettings.lastBackupDate)}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={handleBackupNow} icon={<Download className="h-4 w-4" />}>
                          Backup Now
                        </Button>
                        <Button variant="outline" onClick={handleRestoreBackup} icon={<Upload className="h-4 w-4" />}>
                          Restore
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Automated Backup */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Backup</h3>
                    <div className="space-y-4">
                      <Checkbox
                        label="Enable Automated Backups"
                        checked={backupSettings.autoBackupEnabled}
                        onChange={(e) => {
                          setBackupSettings({ ...backupSettings, autoBackupEnabled: e.target.checked });
                          setHasUnsavedChanges(true);
                        }}
                        helpText="Automatically backup system data on schedule"
                      />
                      {backupSettings.autoBackupEnabled && (
                        <div className="ml-6 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <Select
                              label="Backup Frequency"
                              value={backupSettings.backupFrequency}
                              onChange={(e) => {
                                setBackupSettings({ ...backupSettings, backupFrequency: e.target.value });
                                setHasUnsavedChanges(true);
                              }}
                              options={[
                                { value: 'hourly', label: 'Hourly' },
                                { value: 'daily', label: 'Daily' },
                                { value: 'weekly', label: 'Weekly' },
                                { value: 'monthly', label: 'Monthly' },
                              ]}
                            />
                            <Input
                              label="Backup Time"
                              type="time"
                              icon={<Clock className="h-4 w-4" />}
                              value={backupSettings.backupTime}
                              onChange={(e) => {
                                setBackupSettings({ ...backupSettings, backupTime: e.target.value });
                                setHasUnsavedChanges(true);
                              }}
                            />
                            <Input
                              label="Retention Period"
                              type="number"
                              min="7"
                              max="365"
                              value={backupSettings.retentionPeriod}
                              onChange={(e) => {
                                setBackupSettings({ ...backupSettings, retentionPeriod: e.target.value });
                                setHasUnsavedChanges(true);
                              }}
                              helpText="Days"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Backup Information */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                      <Database className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-1">Backup Information</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Backups include all database records, user data, and uploaded files</li>
                          <li>• Backups are encrypted and stored securely</li>
                          <li>• You can restore from any backup within the retention period</li>
                          <li>• Manual backups do not count towards retention limits</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Calendar Settings */}
          {activeTab === 'calendar' && (
            <div className="space-y-6">
              <Card>
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h2 className="text-xl font-bold text-ncaa-dark flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-ncaa-primary" />
                    Booking Calendar Management
                  </h2>
                  <p className="text-sm text-ncaa-medium mt-1">
                    Block specific dates to prevent applicants from booking appointments
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Add New Blocked Date */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Block a Date</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <Input
                        label="Select Date"
                        type="date"
                        icon={<Calendar className="h-4 w-4" />}
                        value={newBlockedDate}
                        onChange={(e) => setNewBlockedDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <Input
                        label="Reason for Blocking"
                        icon={<Ban className="h-4 w-4" />}
                        value={newBlockedReason}
                        onChange={(e) => setNewBlockedReason(e.target.value)}
                        placeholder="e.g., Public Holiday, Maintenance Day"
                      />
                    </div>
                    <Button
                      onClick={handleAddBlockedDate}
                      icon={<Plus className="h-4 w-4" />}
                    >
                      Add Blocked Date
                    </Button>
                  </div>

                  {/* Blocked Dates List */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Blocked Dates ({blockedDates.length})
                      </h3>
                      <Badge variant="info">
                        {blockedDates.length} {blockedDates.length === 1 ? 'date' : 'dates'} blocked
                      </Badge>
                    </div>

                    {blockedDates.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                        <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Blocked Dates</h3>
                        <p className="text-gray-600">Add dates above to block them from booking</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {blockedDates
                          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                          .map((blocked) => {
                            const isPast = new Date(blocked.date) < new Date();
                            return (
                              <div
                                key={blocked.id}
                                className={`flex items-center justify-between p-4 bg-white border rounded-lg ${
                                  isPast ? 'border-gray-200 opacity-60' : 'border-red-200 bg-red-50'
                                }`}
                              >
                                <div className="flex items-center gap-4 flex-1">
                                  <div className="flex-shrink-0">
                                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                                      <Ban className="h-6 w-6 text-red-600" />
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="text-lg font-semibold text-gray-900">
                                        {new Date(blocked.date).toLocaleDateString('en-US', {
                                          weekday: 'long',
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric',
                                        })}
                                      </p>
                                      {isPast && (
                                        <Badge variant="default" size="sm">
                                          Past
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-600">{blocked.reason}</p>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRemoveBlockedDate(blocked.id)}
                                  icon={<X className="h-4 w-4" />}
                                  className="text-red-600 hover:bg-red-50 border-red-300"
                                >
                                  Remove
                                </Button>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>

                  {/* Quick Add Common Holidays */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-3">Quick Add: Common Holidays</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { date: '2025-01-01', reason: 'New Year\'s Day' },
                        { date: '2025-04-18', reason: 'Good Friday' },
                        { date: '2025-04-21', reason: 'Easter Monday' },
                        { date: '2025-05-01', reason: 'Workers\' Day' },
                        { date: '2025-06-12', reason: 'Democracy Day' },
                        { date: '2025-10-01', reason: 'Independence Day' },
                        { date: '2025-12-25', reason: 'Christmas Day' },
                        { date: '2025-12-26', reason: 'Boxing Day' },
                      ].map((holiday) => {
                        const isAlreadyBlocked = blockedDates.some((d) => d.date === holiday.date);
                        return (
                          <button
                            key={holiday.date}
                            onClick={() => {
                              if (!isAlreadyBlocked) {
                                setNewBlockedDate(holiday.date);
                                setNewBlockedReason(holiday.reason);
                              }
                            }}
                            disabled={isAlreadyBlocked}
                            className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                              isAlreadyBlocked
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-white text-blue-700 hover:bg-blue-100 border border-blue-300'
                            }`}
                          >
                            <div className="font-medium">{holiday.reason}</div>
                            <div className="text-xs opacity-75">
                              {new Date(holiday.date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-blue-700 mt-3">
                      Click a holiday to add it to the form above, then click "Add Blocked Date"
                    </p>
                  </div>

                  {/* Information */}
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Important Notes</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          <li>• Blocked dates will not be available for applicants when booking appointments</li>
                          <li>• Existing appointments on blocked dates will not be automatically cancelled</li>
                          <li>• Past dates can be removed from the list but serve as historical records</li>
                          <li>• Changes take effect immediately after saving</li>
                          <li>• Applicants will see blocked dates marked as unavailable in the booking calendar</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
